import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { OrgResponse } from "@bw/shared/types";
import { formatDate } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName } from "@/shared/orgDisplay";

import { listOnboardingOrgs } from "../api";

const FILTERS = ["All", "Pending Review", "Accepted", "Overdue"] as const;
const PAGE_SIZE = 15;
type AcceptanceFilter = (typeof FILTERS)[number];

function daysSince(iso: string): number {
  const createdAt = new Date(iso);
  return Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
}

function matchesFilter(org: OrgResponse, filter: AcceptanceFilter): boolean {
  if (filter === "All") return true;
  if (filter === "Accepted") {
    return Boolean(org.contract_terms_accepted_at);
  }
  if (filter === "Pending Review") {
    return (
      org.status === "pending" ||
      org.status === "in_review" ||
      org.status === "approved"
    );
  }

  return (
    !org.contract_terms_accepted_at &&
    (org.status === "pending" || org.status === "in_review") &&
    daysSince(org.created_at) > 14
  );
}

function paginationLabel(type: string, page: number | null): string {
  if (type === "next") return "Next";
  if (type === "previous") return "Previous";
  if (type === "first") return "First";
  if (type === "last") return "Last";
  return page === null ? "More pages" : `Page ${page}`;
}

export function AcceptanceTimelinePage() {
  const [activeFilter, setActiveFilter] = useState<AcceptanceFilter>("All");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform", "onboarding"],
    queryFn: listOnboardingOrgs,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const allOrgs: OrgResponse[] = [
      ...data.pending,
      ...data.in_review,
      ...data.approved,
    ];
    return allOrgs.filter((org) => matchesFilter(org, activeFilter));
  }, [activeFilter, data]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  return (
    <PlatformPage title="Contract Acceptance Pipeline">
      <Stack spacing={2.5}>
        <Paper
          component="section"
          aria-label="Contract acceptance filters"
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {FILTERS.map((filter) => (
              <Button
                key={filter}
                type="button"
                size="small"
                variant={activeFilter === filter ? "contained" : "outlined"}
                aria-pressed={activeFilter === filter}
                onClick={() => {
                  setActiveFilter(filter);
                  setPage(1);
                }}
              >
                {filter}
              </Button>
            ))}
          </Stack>
        </Paper>

        {isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Could not load contract acceptance organizations. Try again.
          </Alert>
        ) : isLoading ? (
          <Box
            role="status"
            aria-label="Loading contract acceptance organizations"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 8 }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{ py: 6, textAlign: "center" }}
          >
            <Typography color="text.secondary">
              No loaded organizations match the selected filter.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" variant="body2">
              {filtered.length.toLocaleString()} loaded{" "}
              {filtered.length === 1 ? "organization" : "organizations"}
            </Typography>

            <TableContainer
              component={Paper}
              variant="outlined"
              role="region"
              aria-label="Contract acceptance organizations"
              tabIndex={0}
              sx={{
                overflowX: "auto",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Table
                size="small"
                aria-label="Contract acceptance organizations"
                sx={{ minWidth: 760 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Timeline</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Days pending</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((org) => (
                    <TableRow key={org.org_id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <Link
                          component={RouterLink}
                          to={`/organizations/${org.org_id}/contract-versions`}
                          underline="hover"
                        >
                          {orgBusinessName(org)}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {org.contract_terms_accepted_at
                          ? `Accepted ${formatDate(org.contract_terms_accepted_at)}`
                          : `Submitted ${formatDate(org.created_at)}`}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={org.status} />
                      </TableCell>
                      <TableCell align="right">
                        {org.contract_terms_accepted_at
                          ? "—"
                          : `${daysSince(org.created_at)} days`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filtered.length > PAGE_SIZE && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography color="text.secondary" variant="body2">
                    Loaded page {safePage} of {pageCount}
                  </Typography>
                  <Pagination
                    page={safePage}
                    count={pageCount}
                    size="small"
                    getItemAriaLabel={(type, itemPage) =>
                      paginationLabel(type, itemPage)
                    }
                    onChange={(_event, nextPage) => setPage(nextPage)}
                  />
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </PlatformPage>
  );
}
